/**
 * ClipPassVectorEditor · per-clip Pass-Vektor-Zeichenfeld im SequenceLab.
 *
 * Renders the parent sequence's calibration frame as a 2D-tactic-board
 * underneath the clip's tracking row. Admin clicks twice:
 *   1) Pin 1 (Release/Werfer) — pink R-Marker
 *   2) Pin 2 (Catch/Empfänger) — pink C-Marker
 * The SVG arrow connects them live; on Pin 2 we POST the pixel coords to
 * `/api/clips/{id}/pass-vector` and the backend projects via the parent's
 * homography (same math core as YT-Tactics).
 *
 * Hydrates an existing pass_vector on mount so re-opening a clip shows
 * the saved arrow with length + angle.
 */
import React, { useEffect, useRef, useState } from "react";
import { ArrowRightCircle, RotateCcw, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fehlerText, frameUrl, saveClipPassVector, clearClipPassVector } from "@/lib/api";

export default function ClipPassVectorEditor({ clip, parent, onSaved, onClose, getVideoTime }) {
  const cal = parent?.calibration || {};
  // 🆕 Broadcast-Workflow: joint-Anker (frei gezeichnet / dl_auto)
  // zählen als Kalibrierung — Backend löst die passende H zeitbasiert.
  const hasCal = !!(cal.homography || cal.homography_matrix)
    || ((cal.extra_zones || []).some(
      (z) => z?.status === "joint" && z?.homography,
    ));

  // 🐛 HOTFIX 2026-02-24-h · Editor showed the MATCH-START frame
  // (calibration `at_time_s ≈ 0`) → coach saw a static empty field
  // and couldn't tell where the players were at the moment of the
  // pass. The homography is field-bound (works on any frame), so we
  // now default the canvas background to whichever frame is most
  // useful in this order:
  //   1. The live video-player position (where the coach scrubbed to)
  //   2. The clip's start_s (~34.8s for Sequence #3, etc.)
  //   3. The calibration frame (last-resort fallback)
  // A small toggle in the toolbar lets the coach flip back to the
  // calibration frame if the homography lines look off — useful when
  // verifying field-marking alignment.
  const [bgMode, setBgMode] = useState("live");   // "live" | "calibration"
  const liveT = (() => {
    try {
      const vt = getVideoTime?.();
      if (typeof vt === "number" && Number.isFinite(vt) && vt > 0) return vt;
    } catch { /* ignore */ }
    return null;
  })();
  const targetT = bgMode === "calibration"
    ? (cal.at_time_s ?? 0)
    : (liveT ?? clip.start_s ?? cal.at_time_s ?? 0);
  const frameSrc = hasCal ? frameUrl(parent.id, targetT) : null;

  const imgRef = useRef(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [start, setStart] = useState(clip.pass_vector?.start_image_xy || null);
  const [end, setEnd] = useState(clip.pass_vector?.end_image_xy || null);
  const [saving, setSaving] = useState(false);
  // Current persisted vector (hydrates on save → drives length+angle tag)
  const [savedPV, setSavedPV] = useState(clip.pass_vector || null);
  // 🐛 HOTFIX 2026-02-27 · Capture the live video timestamp AT THE
  // MOMENT THE R-PIN IS CLICKED. The previous implementation read
  // `getVideoTime()` at the SECOND click (Catch), by which point
  // the video had naturally played forward to the catch moment —
  // resulting in `release_t` ≈ catch_t (off by the full flight
  // duration). The downstream slice window then anchored at the
  // catch frame, hiding the actual release the coach annotated.
  // Capturing on the FIRST click locks the release timestamp the
  // instant the user pins R, regardless of where the video plays
  // to between clicks.
  const [releaseT, setReleaseT] = useState(null);

  // Phase E Polish · 2026-02-27 — Live Δt readout between R-pin and the
  // current video position. While the coach hovers between the first
  // (R) and second (C) click, this is the only signal that tells them
  // "the R-pin is still locked, the current video frame is N seconds
  // past it". Without this, the coach would scrub forward without any
  // sense of how much flight-time they're accumulating and would
  // accidentally pin C onto the WRONG throw. The colour-coding flags
  // unphysical Δt values:
  //   * Δt < 0          → red    "video is BEFORE the R-pin"
  //   * 0 ≤ Δt < 0.2    → amber  "too early — disc still in hand"
  //   * 0.2 ≤ Δt ≤ 5.0  → emerald"realistic frisbee flight window"
  //   * 5.0 < Δt ≤ 10.0 → amber  "long — but plausible for a Huck"
  //   * Δt > 10.0       → red    "next play? you're past the catch"
  const [liveDeltaT, setLiveDeltaT] = useState(null);
  useEffect(() => {
    // Only poll while R is set but C isn't yet — that's the brief
    // 1-3 s window where the coach is positioning for the C click.
    if (releaseT == null || end != null || !getVideoTime) {
      setLiveDeltaT(null);
      return undefined;
    }
    let raf;
    const tick = () => {
      try {
        const vt = getVideoTime();
        if (typeof vt === "number" && Number.isFinite(vt)) {
          const clipStart = clip.start_s || 0;
          const currentClipT = vt - clipStart;
          setLiveDeltaT(currentClipT - releaseT);
        }
      } catch { /* ignore */ }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [releaseT, end, getVideoTime, clip.start_s]);

  const onImgLoad = () => {
    const im = imgRef.current;
    if (!im) return;
    setNatural({ w: im.naturalWidth, h: im.naturalHeight });
  };

  // Convert click → raw source-pixel coords (the same space the parent's
  // image_points + homography were calibrated in).
  const clickToPixel = (e) => {
    const im = imgRef.current;
    if (!im || !natural.w) return null;
    const rect = im.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * natural.w,
      y: ((e.clientY - rect.top) / rect.height) * natural.h,
    };
  };

  const handleClick = async (e) => {
    if (saving) return;
    const px = clickToPixel(e);
    if (!px) return;
    if (!start) {
      // 🐛 HOTFIX 2026-02-27 · capture release_t at the MOMENT the
      // R-pin is set — NOT at the second click (which is the catch
      // moment + has the video timestamp anywhere up to several
      // seconds past the actual release). Without this lock-in
      // the slice harvester anchors the 30-frame window on the
      // catch frame, hiding the play the coach annotated.
      let capturedReleaseT = null;
      try {
        const vt = getVideoTime?.();
        if (typeof vt === "number" && Number.isFinite(vt)) {
          const clipStart = clip.start_s || 0;
          const clipEnd   = clip.end_s   || (clipStart + 1);
          capturedReleaseT = Math.max(0, Math.min(clipEnd - clipStart, vt - clipStart));
        }
      } catch { /* ignore — backend falls back to clip-midpoint */ }
      setReleaseT(capturedReleaseT);
      setStart(px);
      setEnd(null);
      return;
    }
    if (!end) {
      setEnd(px);
      // Auto-save on second click using the release_t captured at
      // the FIRST click — guarantees the slice harvester anchors at
      // the EXACT frame the coach pinned R on, regardless of any
      // forward video playback between clicks.
      try {
        setSaving(true);
        const res = await saveClipPassVector(
          clip.id, start, px, releaseT,
          natural?.w > 2 ? { w: natural.w, h: natural.h } : null,
        );
        setSavedPV(res.pass_vector);
        toast.success(
          res.pass_vector?.length_m != null
            ? `Pass vector saved · ${res.pass_vector.length_m.toFixed(1)} m`
              + (releaseT != null ? ` · release t=${releaseT.toFixed(2)}s in-clip` : "")
            : "Pass vector saved",
        );
        onSaved?.();
      } catch (err) {
        toast.error(fehlerText(err) || "Save failed");
        setEnd(null);
      } finally {
        setSaving(false);
      }
      return;
    }
    // Already complete → start a fresh vector with this click as
    // new start. Also re-captures release_t for the new draw.
    let capturedReleaseT = null;
    try {
      const vt = getVideoTime?.();
      if (typeof vt === "number" && Number.isFinite(vt)) {
        const clipStart = clip.start_s || 0;
        const clipEnd   = clip.end_s   || (clipStart + 1);
        capturedReleaseT = Math.max(0, Math.min(clipEnd - clipStart, vt - clipStart));
      }
    } catch { /* ignore */ }
    setReleaseT(capturedReleaseT);
    setStart(px); setEnd(null);
  };

  const reset = () => { setStart(null); setEnd(null); setReleaseT(null); };

  const remove = async () => {
    try {
      setSaving(true);
      await clearClipPassVector(clip.id);
      setSavedPV(null); setStart(null); setEnd(null);
      toast.success("Pass vector cleared");
      onSaved?.();
    } catch (err) {
      toast.error(fehlerText(err) || "Clear failed");
    } finally {
      setSaving(false);
    }
  };

  if (!hasCal) {
    return (
      <div className="border border-amber-400/40 bg-amber-400/5 rounded-sm p-4">
        <div className="font-mono text-[11px] uppercase tracking-widest text-amber-300">
          ⚠ Parent-Match hat keine Calibration — bitte erst die Feldecken in Step 2 setzen,
          damit die Homographie die Pixel-Pins in Meter projizieren kann.
        </div>
      </div>
    );
  }

  const pinPct = (p) => p ? ({ left: `${(p.x / natural.w) * 100}%`, top: `${(p.y / natural.h) * 100}%` }) : null;

  return (
    <div data-testid={`clip-pass-vector-editor-${clip.id}`} className="border border-pink-400/40 bg-pink-400/5 rounded-sm overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-pink-400/30">
        <div className="font-mono text-[11px] uppercase tracking-widest text-pink-300 flex items-center gap-2">
          <ArrowRightCircle className="w-3.5 h-3.5" />
          {!start
            ? "Click 1/2 · RELEASE (Werfer)"
            : !end
              ? "Click 2/2 · CATCH (Empfänger)"
              : saving
                ? "Saving…"
                : savedPV
                  ? `Pass saved · ${savedPV.length_m?.toFixed(1)} m · ${Math.round(savedPV.angle_deg || 0)}°`
                  : "Click 1/2 · RELEASE (Werfer)"}
        </div>
        <div className="flex items-center gap-1">
          {/* 🐛 HOTFIX 2026-02-24-h · Background-frame toggle. Default
              "LIVE" shows the frame at the coach's current scrub
              position (or clip.start_s if the player is parked) so the
              pins are placed on the players THEY SEE. "CAL" loads the
              calibration frame for verifying field-marking alignment. */}
          <Button size="sm" variant="ghost"
            data-testid={`clip-pass-vector-bg-toggle-${clip.id}`}
            onClick={() => setBgMode((m) => m === "calibration" ? "live" : "calibration")}
            disabled={saving}
            title={bgMode === "calibration"
              ? "Currently showing calibration frame — click to switch back to live coach view"
              : `Showing frame @ ${targetT.toFixed(1)}s — click to verify field alignment via calibration frame`}
            className={`h-6 px-2 font-mono text-[10px] uppercase tracking-widest disabled:opacity-30 ${
              bgMode === "calibration"
                ? "bg-amber-400/15 text-amber-300 border border-amber-400/40"
                : "bg-emerald-400/10 text-emerald-300 border border-emerald-400/30"
            }`}>
            {bgMode === "calibration" ? "CAL FRAME" : `LIVE @ ${targetT.toFixed(1)}s`}
          </Button>
          <Button size="sm" variant="ghost"
            data-testid={`clip-pass-vector-reset-${clip.id}`}
            onClick={reset} disabled={(!start && !end) || saving}
            className="h-6 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-accent hover:text-primary disabled:opacity-30">
            <RotateCcw className="w-3 h-3 mr-1" /> Reset
          </Button>
          {savedPV && (
            <Button size="sm" variant="ghost"
              data-testid={`clip-pass-vector-clear-${clip.id}`}
              onClick={remove} disabled={saving}
              className="h-6 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-destructive/20 hover:text-destructive disabled:opacity-30">
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose}
            data-testid={`clip-pass-vector-close-${clip.id}`}
            className="h-6 px-2 font-mono text-[10px] uppercase tracking-widest">
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>

      <div className="relative bg-black select-none" onClick={handleClick} style={{ cursor: saving ? "wait" : "crosshair" }}>
        {frameSrc && (
          <img
            ref={imgRef}
            src={frameSrc}
            alt="calibration frame"
            onLoad={onImgLoad}
            className="block w-full h-auto"
            draggable={false}
          />
        )}
        {/* SVG arrow */}
        {start && end && natural.w > 0 && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox={`0 0 ${natural.w} ${natural.h}`}>
            <defs>
              <marker id={`pv-arrow-${clip.id}`} viewBox="0 0 12 12" refX="10" refY="6"
                      markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 12 6 L 0 12 L 3 6 Z" fill="#FF59B2" />
              </marker>
            </defs>
            <line x1={start.x} y1={start.y} x2={end.x} y2={end.y}
                  stroke="rgba(255,89,178,0.25)" strokeWidth={natural.w / 80} strokeLinecap="round" />
            <line x1={start.x} y1={start.y} x2={end.x} y2={end.y}
                  stroke="#FF59B2" strokeWidth={natural.w / 200} strokeLinecap="round"
                  markerEnd={`url(#pv-arrow-${clip.id})`} />
          </svg>
        )}
        {/* R-Pin */}
        {start && (
          <div data-testid="clip-pv-pin-r"
            className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-pink-400 text-black font-display font-black text-[13px] border-2 border-background shadow-lg shadow-pink-400/40 pointer-events-none"
            style={pinPct(start)}>R</div>
        )}
        {/* C-Pin */}
        {end && (
          <div data-testid="clip-pv-pin-c"
            className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-pink-300 text-black font-display font-black text-[13px] border-2 border-background shadow-lg shadow-pink-400/40 pointer-events-none"
            style={pinPct(end)}>C</div>
        )}
      </div>

      <div className="px-3 py-1.5 border-t border-pink-400/30 font-mono text-[9px] uppercase tracking-widest text-muted-foreground flex items-center justify-between gap-3">
        <span className="flex-1 truncate">
          click anywhere on the frame · backend projects via parent&apos;s homography → real-world meters
        </span>
        {/* Phase E Polish · live Δt readout between R-pin and video
            position. Only renders while R is set + C is not — the
            window where the coach needs the temporal anchor most.
            Colour zones mirror typical frisbee flight physics; see
            the comment block where `liveDeltaT` is declared. */}
        {releaseT != null && !end && liveDeltaT != null && (() => {
          const dt = liveDeltaT;
          let colourClass;
          let phrase;
          if (dt < 0) {
            colourClass = "bg-rose-500/20 text-rose-300 border-rose-400/50";
            phrase = "before R · scrub forward";
          } else if (dt < 0.2) {
            colourClass = "bg-amber-300/20 text-amber-200 border-amber-300/50";
            phrase = "too early · disc still in hand";
          } else if (dt <= 5.0) {
            colourClass = "bg-emerald-400/20 text-emerald-200 border-emerald-400/50";
            phrase = "in flight · ready to catch";
          } else if (dt <= 10.0) {
            colourClass = "bg-amber-300/20 text-amber-200 border-amber-300/50";
            phrase = "long · still plausible Huck";
          } else {
            colourClass = "bg-rose-500/20 text-rose-300 border-rose-400/50";
            phrase = "past catch · next play?";
          }
          const sign = dt < 0 ? "−" : "+";
          return (
            <span
              data-testid={`clip-pv-delta-readout-${clip.id}`}
              data-delta-t={dt.toFixed(3)}
              className={`px-2 py-0.5 rounded border font-mono text-[10px] tabular-nums normal-case tracking-normal ${colourClass}`}
              title={`Δt = video_t (${(releaseT + dt).toFixed(2)}s) − R_t (${releaseT.toFixed(2)}s) = ${dt.toFixed(3)}s`}
            >
              📍 R @ {releaseT.toFixed(2)}s · Δ {sign}{Math.abs(dt).toFixed(2)}s · {phrase}
            </span>
          );
        })()}
      </div>
    </div>
  );
}
